// Automationer (spec §6.8, mockup admin-v1/automations.html, owner 2026-11-01): each rule is Var ·
// När · Om · Gör, runs on the server after the moment is recorded, and every run is logged. Today's
// prints are the first rules. E-mail is offered only once a mail service has passed a test.

import { useEffect, useState } from "react";
import { Admin, Api, type AutomationAction, type AutomationIn, type AutomationOut, type AutomationRunOut, type AutomationTrigger, type MailStatus, type PrinterOut } from "../api/client.js";
import { t } from "../i18n.js";

const fmt = (iso: string) => new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "medium" });
const TRIGGERS: AutomationTrigger[] = ["pallet.started", "pallet.closed", "piece.split", "carton.picked", "order.picked", "order.released"];
const DOCS: Extract<AutomationAction, { kind: "print" }>["document"][] = ["sscc-label", "pallet-label", "piece-label", "customer-label", "delivery-note"];
const BUILTIN_PLACES: { key: string; name: string }[] = [{ key: "receiving", name: "Mottagning" }, { key: "packing", name: "Packbord" }, { key: "office", name: "Kontor" }];
const blank = (): AutomationIn => ({ name: "", enabled: true, scope: { flow: null, customerId: null }, trigger: "pallet.closed", guard: null, actions: [{ kind: "print", document: "pallet-label", printerRole: "receiving", printerId: null, copies: 1 }] });

export function Automations() {
  const [list, setList] = useState<AutomationOut[] | null>(null);
  const [places, setPlaces] = useState<{ key: string; name: string }[]>(BUILTIN_PLACES);
  const [mail, setMail] = useState<MailStatus | null>(null);
  const [runs, setRuns] = useState<AutomationRunOut[]>([]);
  const [printers, setPrinters] = useState<PrinterOut[]>([]);
  const [edit, setEdit] = useState<(AutomationIn & { meta?: string }) | null>(null);
  const [guardMsg, setGuardMsg] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [customers, setCustomers] = useState<{ id: string; name: string }[]>([]);

  const load = () => {
    Admin.automations().then((r) => { setList(r.automations); setMail(r.mail); }).catch(() => setList([]));
    Admin.automationRuns().then((r) => setRuns(r.runs)).catch(() => setRuns([]));
  };
  useEffect(() => {
    load();
    Admin.printers().then(setPrinters).catch(() => setPrinters([]));
    Api.bundle().then((b) => { setCustomers(b.parties.filter((p) => p.roles.includes("customer")).sort((a, b2) => a.name.localeCompare(b2.name, "sv"))); const p = b.siteConfig?.printing?.places; if (p?.length) setPlaces(p); }).catch(() => undefined);
    const id = setInterval(() => Admin.automationRuns().then((r) => setRuns(r.runs)).catch(() => undefined), 5000);
    return () => clearInterval(id);
  }, []);

  const save = async () => {
    if (!edit) return;
    const { meta: _m, ...a } = edit;
    try {
      await Admin.saveAutomation({ ...a, guard: a.guard?.trim() ? a.guard.trim() : null });
      setEdit(null);
      setMsg(t("adm.auto.saved", { name: a.name }));
      load();
    } catch (e) { setMsg(e instanceof Error ? e.message : String(e)); }
  };
  const toggle = async (a: AutomationOut) => {
    const { version: _v, seeded: _s, updatedBy: _u, updatedAt: _t, lastRun: _l, ...rest } = a;
    await Admin.saveAutomation({ ...rest, enabled: !a.enabled }).catch((e: unknown) => setMsg(e instanceof Error ? e.message : String(e)));
    load();
  };
  const remove = async (a: AutomationOut) => {
    if (!window.confirm(t("adm.auto.removeConfirm", { name: a.name }))) return;
    await Admin.deleteAutomation(a.id).catch(() => undefined);
    if (edit?.id === a.id) setEdit(null);
    load();
  };
  // Rehearsal: the rule as it stands in the editor over the last week's real moments, nothing done (R6.19).
  const [rehearsal, setRehearsal] = useState<Awaited<ReturnType<typeof Admin.rehearseAutomation>> | null>(null);
  const rehearse = async () => {
    if (!edit) return;
    setRehearsal(null);
    try { setRehearsal(await Admin.rehearseAutomation({ ...edit, guard: edit.guard?.trim() ? edit.guard.trim() : null })); } catch (e) { setMsg(e instanceof Error ? e.message : String(e)); }
  };
  const checkGuard = async (g: string) => {
    if (!g.trim()) { setGuardMsg(null); return; }
    const r = await Admin.checkGuard(g).catch(() => null);
    setGuardMsg(r && !r.ok ? r.message ?? t("adm.auto.guardBad") : null);
  };

  const trig = (x: AutomationTrigger) => t(`adm.auto.trig.${x}` as "adm.auto.trig.pallet.closed");
  const docName = (d: string) => t(`adm.auto.doc.${d}` as "adm.auto.doc.pallet-label");
  const roleName = (r: string) => places.find((p) => p.key === r)?.name ?? r;
  const ROLES = places.map((p) => p.key);
  const where = (a: AutomationIn) => [a.scope.flow ? t(`adm.auto.flow.${a.scope.flow}` as "adm.auto.flow.picking") : null, a.scope.customerId ? customers.find((c) => c.id === a.scope.customerId)?.name ?? t("adm.auto.oneCustomer") : null].filter(Boolean).join(" · ") || t("adm.auto.everywhere");
  const doText = (x: AutomationAction) =>
    x.kind === "print" ? t("adm.auto.do.print", { doc: docName(x.document), on: x.printerId ? printers.find((p) => p.id === x.printerId)?.name ?? "?" : t("adm.auto.role", { role: roleName(x.printerRole ?? "receiving") }), n: x.copies })
    : x.kind === "email" ? t("adm.auto.do.email", { to: x.to })
    : t("adm.auto.do.webhook", { host: (() => { try { return new URL(x.url).host; } catch { return x.url; } })() });
  const lastRun = (a: AutomationOut) => (a.lastRun ? <><span className={`pill ${a.lastRun.status === "ok" ? "ok" : a.lastRun.status === "failed" ? "bad" : "mut"}`}>{t(`adm.auto.st.${a.lastRun.status}` as "adm.auto.st.ok")}</span><div className="mut" style={{ fontSize: 12 }}>{fmt(a.lastRun.at)}</div></> : <span className="mut">—</span>);
  const eventText = (r: AutomationRunOut) => {
    const d = r.data as { sscc?: string; containerId?: string; reference?: string; piece?: { lot?: string; quantity?: string; uom?: string } };
    const tail = d.reference ? ` · ${t("adm.auto.order", { ref: d.reference })}` : d.sscc ? ` · ${d.sscc}` : d.piece ? ` · ${d.piece.lot} · ${d.piece.quantity} ${d.piece.uom}` : "";
    return `${trig(r.trigger)}${tail}`;
  };

  const setAction = (i: number, x: AutomationAction) => edit && setEdit({ ...edit, actions: edit.actions.map((y, j) => (j === i ? x : y)) });
  const addAction = (kind: AutomationAction["kind"]) => {
    if (!edit) return;
    const x: AutomationAction = kind === "print" ? { kind, document: "pallet-label", printerRole: "receiving", printerId: null, copies: 1 } : kind === "email" ? { kind, to: "", subject: "", body: "", attach: "none" } : { kind, url: "https://", secret: "" };
    setEdit({ ...edit, actions: [...edit.actions, x] });
  };
  const mailReady = !!mail?.verifiedAt;

  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.automations")}</h1><div className="crumb">{t("adm.auto.sub", { n: list?.filter((a) => a.enabled).length ?? 0 })}</div></div>
        <div className="right"><button type="button" className="abtn primary" onClick={() => { setEdit(blank()); setGuardMsg(null); }}>{t("adm.auto.new")}</button></div>
      </div>
      <div className="adm-content">
        {msg && <p className="ahint" style={{ color: "var(--ok)" }}>{msg}</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {edit && (
            <div className="card">
              <div className="hd">{edit.id ? edit.name : t("adm.auto.new")} {edit.meta && <span className="sub">{edit.meta}</span>}</div>
              <div className="bd">
                <div className="auto-ed">
                  <div className="auto-step"><b>{t("adm.auto.name")}</b><input className="ain" value={edit.name} placeholder={t("adm.auto.namePh")} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
                  <div className="auto-step"><b>{t("adm.auto.where")}</b>
                    <div className="auto-row">
                      <select className="ain" value={edit.scope.flow ?? ""} onChange={(e) => setEdit({ ...edit, scope: { ...edit.scope, flow: (e.target.value || null) as AutomationIn["scope"]["flow"] } })}>
                        <option value="">{t("adm.auto.allFlows")}</option><option value="receiving">{t("adm.auto.flow.receiving")}</option><option value="picking">{t("adm.auto.flow.picking")}</option><option value="move">{t("adm.auto.flow.move")}</option>
                      </select>
                      <select className="ain" value={edit.scope.customerId ?? ""} onChange={(e) => setEdit({ ...edit, scope: { ...edit.scope, customerId: e.target.value || null } })}>
                        <option value="">{t("adm.auto.allCustomers")}</option>
                        {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="hint">{t("adm.auto.whereHint")}</div>
                  </div>
                  <div className="auto-step"><b>{t("adm.auto.when")}</b>
                    <select className="ain" value={edit.trigger} onChange={(e) => setEdit({ ...edit, trigger: e.target.value as AutomationTrigger })}>
                      {TRIGGERS.map((x) => <option key={x} value={x}>{trig(x)}</option>)}
                    </select>
                    <div className="hint">{t(`adm.auto.trigHint.${edit.trigger}` as "adm.auto.trigHint.pallet.closed")}</div>
                  </div>
                  <div className="auto-step"><b>{t("adm.auto.if")}</b>
                    <input className="ain mono" value={edit.guard ?? ""} placeholder={t("adm.auto.guardPh")} onChange={(e) => setEdit({ ...edit, guard: e.target.value })} onBlur={(e) => void checkGuard(e.target.value)} />
                    <div className="hint" style={guardMsg ? { color: "var(--bad)" } : undefined}>{guardMsg ?? t("adm.auto.guardHint")}</div>
                  </div>
                  <div className="auto-step"><b>{t("adm.auto.do")}</b>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {edit.actions.map((x, i) => (
                        <div key={i} className="auto-row">
                          <span className="mut">{i + 1}.</span>
                          {x.kind === "print" && <>
                            <span>{t("adm.auto.print")}</span>
                            <select className="ain" value={x.document} onChange={(e) => setAction(i, { ...x, document: e.target.value as typeof x.document })}>{DOCS.map((d) => <option key={d} value={d}>{docName(d)}</option>)}</select>
                            <span>{t("adm.auto.on")}</span>
                            <select className="ain" value={x.printerId ?? `role:${x.printerRole ?? "receiving"}`} onChange={(e) => { const v = e.target.value; setAction(i, v.startsWith("role:") ? { ...x, printerRole: v.slice(5), printerId: null } : { ...x, printerId: v, printerRole: null }); }}>
                              {ROLES.map((r) => <option key={r} value={`role:${r}`}>{t("adm.auto.role", { role: roleName(r) })}</option>)}
                              {printers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                            <input className="ain" type="number" min={1} max={10} style={{ width: 64 }} value={x.copies} onChange={(e) => setAction(i, { ...x, copies: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })} /><span>{t("adm.auto.copies")}</span>
                            {x.document === "delivery-note" && <div className="hint" style={{ flexBasis: "100%" }}>{t("adm.auto.pdfLater")}</div>}
                          </>}
                          {x.kind === "email" && <>
                            <span>{t("adm.auto.mailTo")}</span>
                            <input className="ain" style={{ flex: 1, minWidth: 180 }} value={x.to} placeholder="order@kund.se" onChange={(e) => setAction(i, { ...x, to: e.target.value })} />
                            <input className="ain" style={{ flexBasis: "100%" }} value={x.subject} placeholder={t("adm.auto.subjectPh")} onChange={(e) => setAction(i, { ...x, subject: e.target.value })} />
                            <textarea className="ain" style={{ flexBasis: "100%", minHeight: 70, padding: 8 }} value={x.body} placeholder={t("adm.auto.bodyPh")} onChange={(e) => setAction(i, { ...x, body: e.target.value })} />
                            {(edit.trigger === "order.picked" || edit.trigger === "order.released") && <label><input type="checkbox" checked={x.attach === "delivery-note"} onChange={(e) => setAction(i, { ...x, attach: e.target.checked ? "delivery-note" : "none" })} /> {t("adm.auto.attachNote")}</label>}
                          </>}
                          {x.kind === "webhook" && <>
                            <span>{t("adm.auto.webhook")}</span>
                            <input className="ain mono" style={{ flex: 1, minWidth: 240 }} value={x.url} onChange={(e) => setAction(i, { ...x, url: e.target.value })} />
                            <input className="ain mono" style={{ width: 200 }} value={x.secret} placeholder={t("adm.auto.secretPh")} onChange={(e) => setAction(i, { ...x, secret: e.target.value })} />
                            <div className="hint" style={{ flexBasis: "100%" }}>{t("adm.auto.webhookHint")}</div>
                          </>}
                          {edit.actions.length > 1 && <button type="button" className="abtn sm" onClick={() => setEdit({ ...edit, actions: edit.actions.filter((_, j) => j !== i) })}>{t("adm.auto.removeAction")}</button>}
                        </div>
                      ))}
                      <div className="auto-row"><span className="mut">{t("adm.auto.addAction")}</span>
                        <button type="button" className="abtn sm" onClick={() => addAction("print")}>{t("adm.auto.print")}</button>
                        <button type="button" className="abtn sm" onClick={() => addAction("webhook")}>{t("adm.auto.webhook")}</button>
                        {mailReady && <button type="button" className="abtn sm" onClick={() => addAction("email")}>{t("adm.auto.email")}</button>}
                        {!mailReady && <span className="hint">{t("adm.auto.mailHidden")}</span>}
                      </div>
                    </div>
                  </div>
                  <label className="auto-step"><b>{t("adm.auto.active")}</b><span><input type="checkbox" checked={edit.enabled} onChange={(e) => setEdit({ ...edit, enabled: e.target.checked })} /> {t("adm.auto.activeHint")}</span></label>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                  <button type="button" className="abtn primary" disabled={!edit.name.trim() || !!guardMsg} onClick={() => void save()}>{t("adm.auto.save")}</button>
                  <button type="button" className="abtn" disabled={!!guardMsg} onClick={() => void rehearse()}>{t("adm.auto.rehearse")}</button>
                  <button type="button" className="abtn" onClick={() => { setEdit(null); setRehearsal(null); }}>{t("adm.auto.cancel")}</button>
                </div>
                {rehearsal && (
                  <div className="card" style={{ marginTop: 12 }}>
                    <div className="hd">{t("adm.auto.rehearsal")} <span className="sub">{t("adm.auto.rehearsal.sub", { fires: rehearsal.fires, total: rehearsal.total })}</span></div>
                    <div className="bd flush">
                      <table className="at">
                        <thead><tr><th>{t("adm.auto.rehearsal.when")}</th><th>{t("adm.auto.rehearsal.what")}</th><th>{t("adm.auto.rehearsal.fires")}</th><th>{t("adm.auto.rehearsal.actions")}</th></tr></thead>
                        <tbody>
                          {rehearsal.rows.length === 0 && <tr><td colSpan={4} className="mut">{t("adm.auto.rehearsal.none")}</td></tr>}
                          {rehearsal.rows.slice(0, 50).map((r, i) => (
                            <tr key={i}>
                              <td className="mut">{new Date(r.at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}</td>
                              <td>{r.what || "—"}</td>
                              <td>{r.fires ? <span className="pill ok">{t("adm.auto.true")}</span> : <span className="pill mut">{!r.inScope ? t("adm.auto.rehearsal.outOfScope") : t("adm.auto.rehearsal.guardNo")}</span>}</td>
                              <td className="mut" style={{ fontSize: 12 }}>{r.actions.join(" · ")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="card">
            <div className="bd flush">
              <table className="at">
                <thead><tr><th>{t("adm.auto.col.name")}</th><th>{t("adm.auto.where")}</th><th>{t("adm.auto.when")}</th><th>{t("adm.auto.do")}</th><th>{t("adm.auto.col.last")}</th><th></th></tr></thead>
                <tbody>
                  {list?.length === 0 && <tr><td colSpan={6} className="mut">{t("adm.auto.none")}</td></tr>}
                  {list?.map((a) => (
                    <tr key={a.id} style={a.enabled ? undefined : { opacity: 0.6 }}>
                      <td><b>{a.name}</b>{a.seeded && <div className="mut" style={{ fontSize: 12 }}>{t("adm.auto.seeded")}</div>}</td>
                      <td>{where(a)}{a.guard && <div className="id mut" style={{ fontSize: 12 }}>{t("adm.auto.ifShort")} {a.guard}</div>}</td>
                      <td>{trig(a.trigger)}</td>
                      <td>{a.actions.map((x, i) => <div key={i}>{doText(x)}</div>)}</td>
                      <td>{lastRun(a)}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button type="button" className={`abtn sm${a.enabled ? " primary" : ""}`} onClick={() => void toggle(a)}>{a.enabled ? t("adm.auto.on.yes") : t("adm.auto.on.no")}</button>{" "}
                        <button type="button" className="abtn sm" onClick={() => { const { version: _v, seeded: _s, updatedBy, updatedAt, lastRun: _l, ...rest } = a; setEdit({ ...rest, meta: t("adm.auto.meta", { v: a.version, by: updatedBy ?? "—", at: fmt(updatedAt) }) }); setGuardMsg(null); }}>{t("adm.prn.editBtn")}</button>{" "}
                        <button type="button" className="abtn sm" onClick={() => void remove(a)}>{t("adm.prn.remove")}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card">
            <div className="hd">{t("adm.auto.log")} <span className="sub">{t("adm.auto.logSub")}</span></div>
            <div className="bd flush">
              <table className="at">
                <thead><tr><th>{t("adm.auto.col.at")}</th><th>{t("adm.auto.col.name")}</th><th>{t("adm.auto.col.event")}</th><th>{t("adm.auto.if")}</th><th>{t("adm.auto.col.result")}</th></tr></thead>
                <tbody>
                  {runs.length === 0 && <tr><td colSpan={5} className="mut">{t("adm.auto.noRuns")}</td></tr>}
                  {runs.map((r) => (
                    <tr key={r.id}>
                      <td className="id">{fmt(r.at)}</td><td>{r.automation}</td><td>{eventText(r)}</td>
                      <td>{r.guard === null ? "—" : r.guard ? t("adm.auto.true") : t("adm.auto.false")}</td>
                      <td><span className={`pill ${r.status === "ok" ? "ok" : r.status === "failed" ? "bad" : "mut"}`}>{t(`adm.auto.st.${r.status}` as "adm.auto.st.ok")}</span>{r.outcome.map((o, i) => <div key={i} className="mut" style={{ fontSize: 12, color: o.ok ? undefined : "var(--bad)" }}>{o.detail}</div>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <MailCard status={mail} onChange={setMail} />
        </div>
      </div>
    </>
  );
}

/** The mail service the e-mail action sends through. Settings are company config; the password goes
 *  to the server once and is never shown again; a test mail proves the settings. */
function MailCard({ status, onChange }: { status: MailStatus | null; onChange: (m: MailStatus) => void }) {
  const [cfg, setCfg] = useState<Record<string, unknown> | null>(null);
  const [password, setPassword] = useState("");
  const [to, setTo] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => { Admin.tenantConfig().then((r) => setCfg(r.config as unknown as Record<string, unknown>)).catch(() => setCfg(null)); }, []);
  if (!cfg) return null;
  const mail = (cfg["mail"] ?? { kind: "none", from: "", smtpHost: "", smtpPort: 587, smtpUser: "" }) as { kind: "none" | "smtp" | "graph"; from: string; smtpHost: string; smtpPort: number; smtpUser: string };
  const set = (m: Partial<typeof mail>) => setCfg({ ...cfg, mail: { ...mail, ...m } });
  const save = async () => {
    try {
      await Admin.putTenantConfig(cfg as never);
      if (password) { await Admin.mailPassword(password); setPassword(""); }
      onChange(await Admin.mailStatus());
      setMsg({ ok: true, text: t("adm.mail.saved") });
    } catch (e) { setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) }); }
  };
  const test = async () => {
    try { onChange(await Admin.mailTest(to)); setMsg({ ok: true, text: t("adm.mail.sent", { to }) }); }
    catch (e) { setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) }); }
  };
  return (
    <div className="card">
      <div className="hd">{t("adm.mail.title")} <span className="sub">{status?.verifiedAt ? t("adm.mail.verified", { at: fmt(status.verifiedAt) }) : t("adm.mail.notVerified")}</span></div>
      <div className="bd">
        <div className="aform">
          <span>{t("adm.mail.kind")}</span>
          <select value={mail.kind} onChange={(e) => set({ kind: e.target.value as typeof mail.kind })}>
            <option value="none">{t("adm.mail.none")}</option><option value="smtp">SMTP</option><option value="graph">Microsoft 365</option>
          </select>
          {mail.kind !== "none" && <><span>{t("adm.mail.from")}</span><input className="ain" value={mail.from} placeholder="lager@foretaget.se" onChange={(e) => set({ from: e.target.value })} /></>}
          {mail.kind === "smtp" && <>
            <span>{t("adm.mail.host")}</span><input className="ain" value={mail.smtpHost} placeholder="smtp.foretaget.se" onChange={(e) => set({ smtpHost: e.target.value })} />
            <span>{t("adm.mail.port")}</span><input className="ain" type="number" value={mail.smtpPort} onChange={(e) => set({ smtpPort: Number(e.target.value) || 587 })} />
            <span>{t("adm.mail.user")}</span><input className="ain" value={mail.smtpUser} autoComplete="off" onChange={(e) => set({ smtpUser: e.target.value })} />
            <span>{t("adm.mail.password")}</span><div><input className="ain" type="password" autoComplete="new-password" value={password} placeholder={status?.hasPassword ? t("adm.mail.passwordSet") : ""} onChange={(e) => setPassword(e.target.value)} /><div className="hint">{t("adm.mail.passwordHint")}</div></div>
          </>}
          {mail.kind === "graph" && <><span /><div className="hint">{status?.graphReady ? t("adm.mail.graphHint") : t("adm.mail.graphMissing")}</div></>}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
          <button type="button" className="abtn primary" onClick={() => void save()}>{t("adm.mail.save")}</button>
          {mail.kind !== "none" && <><input className="ain" style={{ width: 240, height: 34 }} value={to} placeholder={t("adm.mail.testTo")} onChange={(e) => setTo(e.target.value)} /><button type="button" className="abtn" disabled={!to.includes("@")} onClick={() => void test()}>{t("adm.mail.test")}</button></>}
        </div>
        {msg && <p className="ahint" style={{ color: msg.ok ? "var(--ok)" : "var(--bad)" }}>{msg.text}</p>}
        <div className="hint" style={{ marginTop: 8 }}>{t("adm.mail.changeHint")}</div>
      </div>
    </div>
  );
}
